import React, { memo } from 'react';
import { Box, Card, Typography, Skeleton } from '@mui/material';
import { BiTrendingUp, BiTrendingDown, BiMinus } from 'react-icons/bi';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface KpiCardProps {
  /** Label displayed as overline above the number */
  title: string;
  /** Primary value — displayed as the large number. Strings are shown as-is. */
  value: string | number;
  /** Unit displayed after the value (e.g., "mi", "min", "mph") */
  unit?: string;
  /** Show skeleton loading state */
  loading?: boolean;
  /** How to format a numeric value. Ignored when value is a string. */
  format?: 'number' | 'compact' | 'currency' | 'percentage' | 'decimal';
  /** Trend percentage — shows directional arrow with semantic color */
  trend?: number;
  /** Text after the trend value (e.g., "vs last week") */
  trendLabel?: string;
  /** Descriptive text below the number */
  subtitle?: string;
  /** Shows "X.X% of total" with semantic color */
  percentage?: number;
  /** Custom content rendered below the number (replaces subtitle/trend/percentage) */
  children?: React.ReactNode;
  /** Makes the card clickable */
  onClick?: () => void;
  /** Visual selected state — primary border + tint bg */
  selected?: boolean;
  /** Attention treatment: a warning-toned border while unselected, so the tile reads as needing action */
  highlight?: boolean;
  /** Accessible label for screen readers */
  'aria-label'?: string;
  /** Size variant: 'large' (default) or 'small' (half-height, compact) */
  size?: 'large' | 'small';
}

// ─── Size Config ────────────────────────────────────────────────────────────

const SIZE_CONFIG = {
  large: {
    padding: 3,
    gap: '4px',
    borderRadius: '12px',
    titleWidth: 80,
    valueFontSize: 40,
    valueLetterSpacing: '-0.03em',
    valueSkeletonWidth: 100,
    unitFontSize: 12,
    footerSkeletonWidth: 120,
    footerFontSize: 12,
    trendIconSize: 18,
    hoverTranslate: 'translateY(-2px)',
  },
  small: {
    padding: '12px 12px 8px 12px',
    gap: '2px',
    borderRadius: '8px',
    titleWidth: 60,
    valueFontSize: 24,
    valueLetterSpacing: '-0.02em',
    valueSkeletonWidth: 60,
    unitFontSize: 10,
    footerSkeletonWidth: 80,
    footerFontSize: 11,
    trendIconSize: 14,
    hoverTranslate: 'translateY(-1px)',
  },
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatValue(value: string | number, format: KpiCardProps['format'] = 'number'): string {
  if (typeof value === 'string') return value;
  if (isNaN(value)) return '0';

  switch (format) {
    case 'compact':
      if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
      if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
      return value.toLocaleString();
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      }).format(value);
    case 'percentage':
      return `${value}%`;
    case 'decimal':
      return value.toFixed(1);
    default:
      return value.toLocaleString();
  }
}

// ─── Shadows (matching design system elevation scale) ───────────────────────

const SHADOW_LEVEL2 = '0px 4px 12px rgba(0,0,0,0.08), 0px 2px 4px rgba(0,0,0,0.04)';
const SHADOW_LEVEL3 = '0px 8px 24px rgba(0,0,0,0.10), 0px 4px 8px rgba(0,0,0,0.05)';

// ─── Component ──────────────────────────────────────────────────────────────

const KpiCard = memo(function KpiCard({
  title,
  value,
  unit,
  loading = false,
  format = 'number',
  trend,
  trendLabel,
  subtitle,
  percentage,
  children,
  onClick,
  selected = false,
  highlight = false,
  'aria-label': ariaLabel,
  size = 'large',
}: KpiCardProps) {
  const s = SIZE_CONFIG[size];

  return (
    <Card
      // A clickable KPI is a control, not a landmark: button role, real keyboard
      // activation, and aria-pressed so "selected" is announced — the visual border
      // alone already misled once (the attention highlight read as selection).
      role={onClick ? 'button' : 'region'}
      aria-pressed={onClick ? selected : undefined}
      aria-label={ariaLabel || `${title}: ${typeof value === 'number' ? formatValue(value, format) : value}`}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={
        onClick
          ? event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: s.gap,
        p: s.padding,
        borderRadius: s.borderRadius,
        bgcolor: (theme) => selected ? `${theme.palette.primary.main}14` : 'background.paper',
        boxShadow: selected ? SHADOW_LEVEL3 : SHADOW_LEVEL2,
        border: '2px solid',
        borderColor: (theme) =>
          selected ? theme.palette.primary.main : highlight ? `${theme.palette.warning.main}80` : 'transparent',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 200ms ease-in-out, box-shadow 200ms ease-in-out',
        '&:hover': {
          transform: onClick ? s.hoverTranslate : undefined,
          boxShadow: onClick ? SHADOW_LEVEL3 : undefined,
          borderColor: (theme) => (!selected && highlight ? theme.palette.warning.main : undefined),
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: '2px',
        },
      }}
    >
      {/* Label */}
      <Typography
        variant='overline'
        sx={{ color: 'grey.500', display: 'block', ...(size === 'small' && { fontSize: 10, lineHeight: 1.2 }) }}
      >
        {loading ? <Skeleton width={s.titleWidth} /> : title}
      </Typography>

      {/* Number */}
      <Typography
        sx={{
          fontSize: s.valueFontSize,
          fontWeight: 800,
          lineHeight: 1.1,
          letterSpacing: s.valueLetterSpacing,
          color: 'text.primary',
        }}
      >
        {loading ? (
          <Skeleton width={s.valueSkeletonWidth} />
        ) : (
          <>
            {formatValue(value, format)}
            {unit && (
              <Typography
                component='span'
                sx={{ fontSize: s.unitFontSize, fontWeight: 400, color: 'grey.500', ml: 0.5 }}
              >
                {unit}
              </Typography>
            )}
          </>
        )}
      </Typography>

      {/* Footer */}
      {loading ? (
        <Skeleton width={s.footerSkeletonWidth} sx={{ fontSize: s.footerFontSize }} />
      ) : children ? (
        children
      ) : (
        <>
          {trend !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: trend > 0 ? 'success.main' : trend < 0 ? 'error.main' : 'grey.500',
                }}
              >
                {Math.abs(trend) < 0.1 ? (
                  <BiMinus size={s.trendIconSize} />
                ) : trend > 0 ? (
                  <BiTrendingUp size={s.trendIconSize} />
                ) : (
                  <BiTrendingDown size={s.trendIconSize} />
                )}
              </Box>
              <Typography
                sx={{
                  fontSize: s.footerFontSize,
                  fontWeight: 600,
                  color: trend > 0 ? 'success.main' : trend < 0 ? 'error.main' : 'grey.500',
                }}
              >
                {Math.abs(trend).toFixed(1)}%
              </Typography>
              {trendLabel && (
                <Typography sx={{ fontSize: s.footerFontSize, color: 'grey.500', ml: '2px' }}>
                  {trendLabel}
                </Typography>
              )}
            </Box>
          )}

          {percentage !== undefined && (
            <Typography
              sx={{
                fontSize: s.footerFontSize,
                color: percentage > 0 ? 'success.main' : 'error.main',
              }}
            >
              {percentage.toFixed(1)}% of total
            </Typography>
          )}

          {subtitle && (
            <Typography sx={{ fontSize: s.footerFontSize, color: 'grey.500' }}>{subtitle}</Typography>
          )}
        </>
      )}
    </Card>
  );
});

export default KpiCard;
